import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import Logger from "utils/logger";

/**
 * Component that ensures toasts are properly positioned on mobile devices
 * This component runs independently of the ad banner to ensure consistent positioning
 */
const MobileToastPositioner = () => {
  useEffect(() => {
    // Only run on mobile platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let toastStyleElement = null;

    const addToastPositioning = () => {
      // Remove any existing toast positioning CSS first
      if (toastStyleElement && toastStyleElement.parentNode) {
        toastStyleElement.parentNode.removeChild(toastStyleElement);
      }

      // Add comprehensive CSS to position toasts above ad banner
      toastStyleElement = document.createElement("style");
      toastStyleElement.textContent = `
        /* Target Material-UI Snackbar containers */
        .notistack-SnackbarContainer,
        .MuiSnackbar-root,
        [class*="SnackbarContainer"],
        [class*="snackbar-container"] {
          bottom: 75px !important;
        }
        
        /* Target individual snackbars */
        .notistack-Snackbar,
        .MuiSnackbar-root,
        [class*="Snackbar"],
        [class*="snackbar"] {
          bottom: 75px !important;
        }
        
        /* Ensure toasts are above the ad banner */
        .notistack-SnackbarContainer,
        .MuiSnackbar-root {
          z-index: 9999 !important;
        }
        
        /* Additional selectors for different toast implementations */
        .MuiAlert-root,
        .MuiSnackbarContent-root,
        [class*="alert"],
        [class*="snackbar"] {
          bottom: 75px !important;
        }
        
        /* Force positioning for any element that might be a toast */
        [class*="toast"],
        [class*="notification"],
        [class*="message"] {
          bottom: 75px !important;
        }
      `;
      document.head.appendChild(toastStyleElement);
      Logger.debug(
        "MobileToastPositioner: Added comprehensive toast positioning CSS"
      );
    };

    // Apply toast positioning immediately
    addToastPositioning();

    // Also apply on window resize to handle orientation changes
    const handleResize = () => {
      addToastPositioning();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      // Don't remove the CSS - let it persist for the session
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default MobileToastPositioner;
